import * as v from "@valibot/valibot";

// ============================================================
// API Schemas
// ============================================================

import { Hex, UnsignedInteger } from "../../_schemas.ts";

/**
 * Set user abstraction mode (method for agent wallet).
 *
 * Like {@link userSetAbstraction} but signed as an L1 action by the agent wallet (instead of EIP-712 by the principal).
 *
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/exchange-endpoint#set-user-abstraction-agent
 */
export const AgentSetAbstractionRequest = /* @__PURE__ */ (() => {
  return v.object({
    /** Action to perform. */
    action: v.object({
      /** Type of action. */
      type: v.literal("agentSetAbstraction"),
      /**
       * User abstraction mode.
       * - `"i"`: disabled
       * - `"u"`: unifiedAccount
       * - `"p"`: portfolioMargin
       */
      abstraction: v.picklist(["i", "u", "p"]),
    }),
    /** Nonce (timestamp in ms) used to prevent replay attacks. */
    nonce: UnsignedInteger,
    /** ECDSA signature components. */
    signature: v.object({
      /** First 32-byte component. */
      r: v.pipe(Hex, v.length(66)),
      /** Second 32-byte component. */
      s: v.pipe(Hex, v.length(66)),
      /** Recovery identifier. */
      v: v.picklist([27, 28]),
    }),
    /** Expiration time of the action. */
    expiresAfter: v.optional(UnsignedInteger),
  });
})();
export type AgentSetAbstractionRequest = v.InferOutput<typeof AgentSetAbstractionRequest>;

/**
 * Successful response without specific data or error response.
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/exchange-endpoint#set-user-abstraction-agent
 */
export type AgentSetAbstractionResponse =
  | {
    /** Successful status. */
    status: "ok";
    /** Response details. */
    response: {
      /** Type of response. */
      type: "default";
    };
  }
  | {
    /** Error status. */
    status: "err";
    /** Error message. */
    response: string;
  };

// ============================================================
// Execution Logic
// ============================================================

import { parse } from "../../../_base.ts";
import { canonicalize } from "../../../signing/mod.ts";
import {
  type ExchangeConfig,
  type ExcludeErrorResponse,
  executeL1Action,
  type ExtractRequestOptions,
} from "./_base/mod.ts";

/** Schema for action fields (excludes request-level system fields). */
const AgentSetAbstractionActionSchema = /* @__PURE__ */ (() => {
  return v.object(AgentSetAbstractionRequest.entries.action.entries);
})();

/** Action parameters for the {@linkcode agentSetAbstraction} function. */
export type AgentSetAbstractionParameters = Omit<v.InferInput<typeof AgentSetAbstractionActionSchema>, "type">;

/** Request options for the {@linkcode agentSetAbstraction} function. */
export type AgentSetAbstractionOptions = ExtractRequestOptions<
  v.InferInput<typeof AgentSetAbstractionRequest>
>;

/** Successful variant of {@linkcode AgentSetAbstractionResponse} without errors. */
export type AgentSetAbstractionSuccessResponse = ExcludeErrorResponse<AgentSetAbstractionResponse>;

/**
 * Set user abstraction mode (method for agent wallet).
 *
 * Like {@link userSetAbstraction} but signed as an L1 action by the agent wallet (instead of EIP-712 by the principal).
 *
 * Signing: L1 Action.
 *
 * @param config General configuration for Exchange API requests.
 * @param params Parameters specific to the API request.
 * @param opts Request execution options.
 * @return Successful response without specific data.
 *
 * @throws {ValidationError} When the request parameters fail validation (before sending).
 * @throws {TransportError} When the transport layer throws an error.
 * @throws {ApiRequestError} When the API returns an unsuccessful response.
 *
 * @example
 * ```ts
 * import { HttpTransport } from "@nktkas/hyperliquid";
 * import { agentSetAbstraction } from "@nktkas/hyperliquid/api/exchange";
 * import { privateKeyToAccount } from "npm:viem/accounts";
 *
 * const wallet = privateKeyToAccount("0x..."); // viem or ethers
 * const transport = new HttpTransport(); // or `WebSocketTransport`
 *
 * await agentSetAbstraction({ transport, wallet }, {
 *   abstraction: "u",
 * });
 * ```
 *
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/exchange-endpoint#set-user-abstraction-agent
 */
export function agentSetAbstraction(
  config: ExchangeConfig,
  params: AgentSetAbstractionParameters,
  opts?: AgentSetAbstractionOptions,
): Promise<AgentSetAbstractionSuccessResponse> {
  const action = canonicalize(
    AgentSetAbstractionActionSchema,
    parse(AgentSetAbstractionActionSchema, { type: "agentSetAbstraction", ...params }),
  );
  return executeL1Action(config, action, opts);
}
